import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AsyncSaveButton } from "./AsyncSaveButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES } from "@/utils/countries";
import { getCountryFlag } from "@/utils/countryFlags";
import { ALLIANCES, ALLIANCE_GLYPHS } from "@/utils/airlineAlliances";

const AddVendorDialog = ({ open, onOpenChange, vendor, setVendor, onSave, segmentType }) => {
  const isAirline = segmentType === 'flight';
  const label = isAirline ? 'Airline' : 'Vendor';
  // Alliance only applies to airlines. Show the field when EITHER the
  // calling segment is a flight OR the user has explicitly switched the
  // vendor's type to "flight" inside this dialog (the type Select below
  // can be changed independent of the parent segment).
  const showAlliance = vendor.type === "flight" || isAirline;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle data-testid="add-vendor-dialog-title">Add Custom {label}</DialogTitle>
          <DialogDescription>
            Add a new {label.toLowerCase()} to your standardized list.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="vendor-name">{label} Name *</Label>
            <Input
              id="vendor-name"
              data-testid="vendor-name-input"
              placeholder={isAirline
                ? "e.g., Southwest Airlines, JetBlue Airways"
                : "e.g., Holiday Inn, Expedia, Local Tours Inc"
              }
              value={vendor.name}
              onChange={(e) => setVendor({ ...vendor, name: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="vendor-type">Type</Label>
            <Select value={vendor.type} onValueChange={(value) => setVendor({ ...vendor, type: value })}>
              <SelectTrigger id="vendor-type" data-testid="vendor-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flight">Flight / Airline</SelectItem>
                <SelectItem value="train">Train</SelectItem>
                <SelectItem value="bus">Bus</SelectItem>
                <SelectItem value="cruise">Cruise</SelectItem>
                <SelectItem value="car_rental">Car Rental</SelectItem>
                <SelectItem value="rideshare">Rideshare</SelectItem>
                <SelectItem value="accommodation">Accommodation</SelectItem>
                <SelectItem value="activity">Activity / Event</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="vendor-country">
              Country <span className="text-gray-400 font-normal">(Optional — HQ for global brands)</span>
            </Label>
            <Select
              value={vendor.country || "__unset"}
              onValueChange={(value) => setVendor({ ...vendor, country: value === "__unset" ? "" : value })}
            >
              <SelectTrigger id="vendor-country" data-testid="vendor-country-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__unset">— None —</SelectItem>
                {COUNTRIES.map(c => (
                  <SelectItem key={c} value={c}>
                    {getCountryFlag(c)} {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showAlliance && (
            <div className="grid gap-2">
              <Label htmlFor="vendor-alliance">
                Alliance <span className="text-gray-400 font-normal">(Optional)</span>
              </Label>
              <Select
                value={vendor.alliance || "__unset"}
                onValueChange={(value) => setVendor({ ...vendor, alliance: value === "__unset" ? "" : value })}
              >
                <SelectTrigger id="vendor-alliance" data-testid="vendor-alliance-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset">— None —</SelectItem>
                  {ALLIANCES.map(a => (
                    <SelectItem key={a} value={a}>
                      <span className="mr-1">{ALLIANCE_GLYPHS[a]}</span>{a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="vendor-notes">Notes (Optional)</Label>
            <Textarea
              id="vendor-notes"
              data-testid="vendor-notes-input"
              placeholder="Additional information about this vendor..."
              value={vendor.notes}
              onChange={(e) => setVendor({ ...vendor, notes: e.target.value })}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vendor-cancel-btn">
            Cancel
          </Button>
          <AsyncSaveButton onSave={onSave} className="bg-sky-500 hover:bg-sky-600" data-testid="vendor-save-btn" savingLabel="Adding…">
            Add {label}
          </AsyncSaveButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddVendorDialog;
